import { ProtectedRoute } from '@/services/auth';
import { Spinner } from '@fluentui/react-components';
import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './Layout';

// Lazy load pages for code splitting
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const ConnectorsPage = lazy(
  () => import('@/features/connectors/ConnectorsPage')
);
const RunsPage = lazy(() => import('@/features/runs/RunsPage'));
const RunDetailsPage = lazy(() => import('@/features/runs/RunDetailsPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const QueuesPage = lazy(() => import('@/features/queues/QueuesPage'));
const PipelineFlowPage = lazy(() => import('@/features/pipeline-flow/PipelineFlowPage'));

// Loading component
const PageLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <Spinner label="Loading..." />
  </div>
);

// 404 component
const NotFoundPage: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      textAlign: 'center',
    }}
  >
    <h1>404 - Page Not Found</h1>
    <p>The page you are looking for does not exist.</p>
    <a href="/">Go to Dashboard</a>
  </div>
);

export const Router: React.FC = () => {
  return (
    <Routes>
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route
          path="/"
          element={
            <Suspense fallback={<PageLoader />}>
              <DashboardPage />
            </Suspense>
          }
        />

        {/* Connectors */}
        <Route
          path="/connectors"
          element={
            <Suspense fallback={<PageLoader />}>
              <ConnectorsPage />
            </Suspense>
          }
        />
        <Route
          path="/connectors/:id"
          element={
            <Suspense fallback={<PageLoader />}>
              <ConnectorsPage />
            </Suspense>
          }
        />

        {/* Runs */}
        <Route
          path="/runs"
          element={
            <Suspense fallback={<PageLoader />}>
              <RunsPage />
            </Suspense>
          }
        />
        <Route
          path="/runs/:id"
          element={
            <Suspense fallback={<PageLoader />}>
              <RunDetailsPage />
            </Suspense>
          }
        />

        {/* Settings */}
        <Route
          path="/settings"
          element={
            <Suspense fallback={<PageLoader />}>
              <SettingsPage />
            </Suspense>
          }
        />

        {/* Queues */}
        <Route
          path="/queues"
          element={
            <Suspense fallback={<PageLoader />}>
              <QueuesPage />
            </Suspense>
          }
        />

        {/* Pipeline Flow */}
        <Route
          path="/pipeline-flow"
          element={
            <Suspense fallback={<PageLoader />}>
              <PipelineFlowPage />
            </Suspense>
          }
        />

        {/* Redirects for old URLs */}
        <Route
          path="/admin/connectors"
          element={<Navigate to="/connectors" replace />}
        />
        <Route path="/admin/runs" element={<Navigate to="/runs" replace />} />
        <Route
          path="/admin/settings"
          element={<Navigate to="/settings" replace />}
        />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
};
